import React, { useState } from 'react';
import { sendContactMessage } from '../../utils/api';

const initialForm = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
};

function ContactForm() {
  const [formData, setFormData] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({
    type: '',
    message: '',
  });

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (feedback.message) {
      setFeedback({ type: '', message: '' });
    }
  };

  const getErrorMessage = (error) => {
    const validationErrors = error?.response?.data?.errors;

    if (validationErrors && typeof validationErrors === 'object') {
      const firstError = Object.values(validationErrors).flat()[0];

      if (firstError) {
        return firstError;
      }
    }

    return (
      error?.response?.data?.message ||
      error?.message ||
      'Your message could not be sent. Please try again.'
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitting) return;

    setSubmitting(true);
    setFeedback({ type: '', message: '' });

    try {
      const response = await sendContactMessage({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
        subject: formData.subject.trim() || null,
        message: formData.message.trim(),
      });

      setFeedback({
        type: 'success',
        message: response?.message || 'Your message has been sent successfully.',
      });

      setFormData(initialForm);
    } catch (error) {
      setFeedback({
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full rounded-xl border border-gray-300 bg-white px-4 py-3 font-montserrat text-gray-900 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:bg-gray-100';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="contact-name" className="block mb-2 font-montserrat font-medium text-gray-700">
            Name
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            className={inputClass}
            maxLength={100}
            required
            disabled={submitting}
          />
        </div>

        <div>
          <label htmlFor="contact-email" className="block mb-2 font-montserrat font-medium text-gray-700">
            Email
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            className={inputClass}
            maxLength={255}
            required
            disabled={submitting}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="contact-phone" className="block mb-2 font-montserrat font-medium text-gray-700">
            Phone
          </label>
          <input
            id="contact-phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            className={inputClass}
            maxLength={30}
            disabled={submitting}
          />
        </div>

        <div>
          <label htmlFor="contact-subject" className="block mb-2 font-montserrat font-medium text-gray-700">
            Subject
          </label>
          <input
            id="contact-subject"
            name="subject"
            type="text"
            value={formData.subject}
            onChange={handleChange}
            className={inputClass}
            maxLength={150}
            disabled={submitting}
          />
        </div>
      </div>

      <div>
        <label htmlFor="contact-message" className="block mb-2 font-montserrat font-medium text-gray-700">
          Message
        </label>
        <textarea
          id="contact-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          className={`${inputClass} min-h-40 resize-y`}
          maxLength={5000}
          required
          disabled={submitting}
        />
      </div>

      {feedback.message && (
        <div
          role="alert"
          className={`rounded-xl px-4 py-3 font-montserrat ${
            feedback.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {feedback.message}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full sm:w-auto rounded-full bg-primary px-8 py-3 font-montserrat font-semibold text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? 'Sending...' : 'Send Message'}
      </button>
    </form>
  );
}

export default ContactForm;
